import { Box, HStack, IconButton, Textarea } from '@chakra-ui/react';
import { memo, useCallback } from 'react';
import { LuArrowUp, LuImage, LuLoaderCircle } from 'react-icons/lu';

import { useAutoResizeTextarea } from 'hooks/useAutoResizeTextarea';

const MIN_ROWS = 1;

interface ChatInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
}

const ChatInput = ({
  value,
  onChange,
  onSubmit,
  placeholder = 'Ask me anything...',
}: ChatInputProps) => {
  const { textareaRef, resize } = useAutoResizeTextarea(value);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e.target.value);
      resize();
    },
    [onChange, resize],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSubmit?.();
      }
    },
    [onSubmit],
  );

  return (
    <Box borderWidth="1px" borderColor="border.light" borderRadius="lg" overflow="hidden" p={2}>
      <Textarea
        p={0}
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        resize="none"
        rows={MIN_ROWS}
        minH="unset"
        overflowY="auto"
        border="none"
        fontSize="sm"
        color="text.primary"
        _focus={{ outline: 'none', boxShadow: 'none' }}
        _placeholder={{ color: 'text.placeholder' }}
      />
      <HStack justifyContent="flex-end" gap={1}>
        <IconButton
          aria-label="Thinking"
          variant="ghost"
          size="2xs"
          color="text.secondary"
          borderRadius="full"
        >
          <LuLoaderCircle />
        </IconButton>
        <IconButton
          aria-label="Attach image"
          variant="ghost"
          size="2xs"
          color="text.secondary"
          borderRadius="full"
        >
          <LuImage />
        </IconButton>
        <IconButton
          aria-label="Submit"
          size="2xs"
          borderRadius="full"
          bg="surface.inverse"
          color="text.inverse"
          _hover={{ opacity: 0.9 }}
          onClick={onSubmit}
        >
          <LuArrowUp />
        </IconButton>
      </HStack>
    </Box>
  );
};

ChatInput.displayName = 'ChatInput';

export default memo(ChatInput);
